import { envelopeData } from '@/lib/envelope'
import { createFileRoute } from '@tanstack/react-router'
import { useSuspenseQuery, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Search } from 'lucide-react'
import { categoriesQueryOptions, searchCoursesQueryOptions } from '@/lib/queries/catalog'
import { CourseCarousel } from '@/components/catalog/course-carousel'
import { CourseCard } from '@/components/catalog/course-card'
import { CatalogSkeleton } from '@/components/shared/loading-skeleton'

export const Route = createFileRoute('/_app/$menu/')({
  loader: async ({ context, params }) => {
    await context.queryClient.prefetchQuery(
      categoriesQueryOptions({ menuSlug: params.menu }),
    )
  },
  component: MenuPage,
  pendingComponent: () => <CatalogSkeleton />,
  errorComponent: () => (
    <div className="text-center py-16">
      <p className="text-muted-foreground">Erro ao carregar conteúdo. Tente recarregar a página.</p>
    </div>
  ),
})

function MenuPage() {
  const { menu } = Route.useParams()
  const [search, setSearch] = useState('')

  const { data: categoriesData } = useSuspenseQuery(categoriesQueryOptions({ menuSlug: menu }))
  const title = categoriesData?.title ?? menu
  const categories = envelopeData(categoriesData) ?? []

  const { data: searchData } = useQuery({
    ...searchCoursesQueryOptions({ q: search }),
    enabled: search.length >= 2,
  })
  const allResults = envelopeData(searchData) ?? []
  const searchResults = allResults.filter((c: any) => c.menuSlug === menu)

  const isSearching = search.length >= 2

  return (
    <div className="space-y-8 max-w-[1600px] mx-auto">
      <div className="space-y-4">
        <h1 className="text-2xl font-bold text-foreground tracking-tight">
          {title}
        </h1>
        <div className="relative max-w-md">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/60" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar..."
            className="w-full h-10 pl-10 pr-4 rounded-full border border-border/60 bg-card/60 text-foreground text-sm placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/40 transition-all"
          />
        </div>
      </div>

      {isSearching ? (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-foreground">
            Resultados para &ldquo;{search}&rdquo;
          </h2>
          {searchResults.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-muted-foreground">Nenhum resultado encontrado.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
              {searchResults.map((course: any) => (
                <CourseCard
                  key={course.courseId}
                  course={{
                    id: course.courseId,
                    title: course.courseName,
                    href: `/${course.menuSlug}/${course.courseSlug}`,
                    thumbnailUrl: course.thumbnailUrl,
                    description: course.description,
                    totalLessons: course.totalLessons,
                    instructorName: course.instructorName,
                    hasAccess: course.hasAccess,
                    price: course.price,
                  }}
                />
              ))}
            </div>
          )}
        </div>
      ) : categories.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-muted-foreground">Nenhum conteudo disponivel.</p>
        </div>
      ) : (
        <div className="space-y-10">
          {categories.map((category: any) => (
            <CourseCarousel
              key={category.categoryId}
              title={category.categoryName}
              courses={category.courses.map((course: any) => ({
                id: course.courseId,
                title: course.courseName,
                href: `/${course.menuSlug}/${course.courseSlug}`,
                thumbnailUrl: course.thumbnailUrl,
                description: course.description,
                totalLessons: course.totalLessons,
                instructorName: course.instructorName,
                hasAccess: course.hasAccess,
                price: course.price,
              }))}
            />
          ))}
        </div>
      )}
    </div>
  )
}
